import { Group, Select } from '@mantine/core';
import { MonthPickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useFinancialsStore } from '../../../../store/financials-store/financials-store';
import {
  setBoard,
  setEndDate,
  setStartDate,
  setTeam,
} from '../../../../store/financials-store/financials-store.actions';
import type { State } from '../../../../store/financials-store/financials-store.type';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { secondaryBase, skyDark } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { H2 } from '../../../../ui-library/typography/typography';
import { usePortfolioProjectsAndBoards } from '../../financials.hooks';

interface DateRangeForm {
  dateRange: [Date | null, Date | null];
  team: string | null;
  board: string | null;
}

export function Header() {
  const portfolio = useGlobalStore((state) => state.portfolio);

  const startDate = useFinancialsStore((state: State) => state.startDate);
  const endDate = useFinancialsStore((state: State) => state.endDate);
  const team = useFinancialsStore((state: State) => state.team);
  const board = useFinancialsStore((state: State) => state.board);

  const [teamOptions, setTeamOptions] = useState<{ label: string; value: string }[]>([]);
  const [boardOptions, setBoardOptions] = useState<{ label: string; value: string }[]>([]);

  const { data: projects } = usePortfolioProjectsAndBoards(portfolio?.id);

  const form = useForm<DateRangeForm>({
    initialValues: {
      dateRange: [
        typeof startDate === 'string' ? new Date(startDate) : startDate instanceof Date ? startDate : null,
        typeof endDate === 'string' ? new Date(endDate) : endDate instanceof Date ? endDate : null,
      ],
      team: team ?? null,
      board: board ?? null,
    },
  });

  // Update team and board options based on form values
  // biome-ignore lint/correctness/useExhaustiveDependencies: <form.setFieldValue is a stable function from Mantine's useForm package>
  useEffect(() => {
    // scenario 1: no team or board selected -> show all team and board options
    if (!form.values.team && !form.values.board) {
      setTeamOptions(projects?.map((project) => ({ label: project.name, value: project.id })) ?? []);
      setBoardOptions(
        projects?.flatMap((project) =>
          project.subprojects.map((subproject) => ({ label: subproject.name, value: subproject.id })),
        ) ?? [],
      );
    }
    // scenario 2: team is selected, so we only show the boards for that team
    else if (form.values.team) {
      setTeamOptions(projects?.map((project) => ({ label: project.name, value: project.id })) ?? []);
      const selectedProject = projects?.find((project) => project.id === form.values.team);
      const validBoardOptions =
        selectedProject?.subprojects.map((subproject) => ({
          label: subproject.name,
          value: subproject.id,
        })) ?? [];
      setBoardOptions(validBoardOptions);

      // If the selected board is not under the selected team, clear it
      if (form.values.board && !validBoardOptions.some((b) => b.value === form.values.board)) {
        form.setFieldValue('board', null);
      }
    }
    // scenario 3: board is selected, show all team options, and set the team form value to the board's parent project if needed
    else if (form.values.board) {
      setTeamOptions(projects?.map((project) => ({ label: project.name, value: project.id })) ?? []);
      setBoardOptions(
        projects?.flatMap((project) =>
          project.subprojects.map((subproject) => ({ label: subproject.name, value: subproject.id })),
        ) ?? [],
      );
      const parentTeamId =
        projects?.find((project) => project.subprojects.find((subproject) => subproject.id === form.values.board))
          ?.id ?? null;
      if (form.values.team !== parentTeamId) {
        form.setFieldValue('team', parentTeamId);
      }
    }
  }, [form.values.team, form.values.board, projects]);

  // Update form when scope dates change
  useEffect(() => {
    const newStartDate =
      typeof startDate === 'string' ? new Date(startDate) : startDate instanceof Date ? startDate : null;
    const newEndDate = typeof endDate === 'string' ? new Date(endDate) : endDate instanceof Date ? endDate : null;

    form.setFieldValue('dateRange', [newStartDate, newEndDate]);
    form.resetDirty();
  }, [startDate, endDate, form.setFieldValue, form.resetDirty]);

  const handleApply = () => {
    const [selectedStartDate, selectedEndDate] = form.values.dateRange;

    setStartDate(selectedStartDate ?? null);

    // endDate should be set to the last day of the month, unless it's the current month;
    // in which case it should be set to the latest non-future date: today's date
    if (selectedEndDate) {
      const endDateDayjs = dayjs(selectedEndDate);
      const isCurrentMonth = endDateDayjs.isSame(dayjs(), 'month');

      const finalEndDate = isCurrentMonth ? dayjs().toDate() : endDateDayjs.endOf('month').toDate();

      setEndDate(finalEndDate);
    } else {
      setEndDate(null);
    }

    setTeam(form.values.team ?? null);
    setBoard(form.values.board ?? null);

    form.resetDirty();
  };

  return (
    <Group>
      <Group>
        <Icon name="payments" color={secondaryBase} size={36} />
        <H2>Financials</H2>
      </Group>
      <Group>
        <Select
          allowDeselect={false}
          placeholder="Select a team"
          clearable={Boolean(!form.values.board)}
          onClear={() => form.setFieldValue('team', null)}
          data={teamOptions}
          radius="xl"
          {...form.getInputProps('team')}
        />
        <Select
          allowDeselect={false}
          placeholder="Select a board"
          clearable
          onClear={() => form.setFieldValue('board', null)}
          data={boardOptions}
          radius="xl"
          {...form.getInputProps('board')}
        />
        <MonthPickerInput
          type="range"
          radius="xl"
          style={{ width: '300px' }}
          leftSection={<Icon name="calendar_month" color={secondaryBase} size={16} />}
          leftSectionPointerEvents="none"
          placeholder="Pick date range"
          value={form.values.dateRange}
          onChange={(value) => form.setFieldValue('dateRange', value)}
          maxDate={dayjs().toDate()}
          clearable
        />
        <Button
          type="submit"
          onClick={handleApply}
          variant="secondary"
          radius="xl"
          size="sm"
          disabled={!form.isDirty()}
          style={{ border: !form.isDirty() ? `1px solid ${skyDark}` : 'none' }}
        >
          Apply
        </Button>
      </Group>
    </Group>
  );
}
